import { create } from "zustand";
import { openDB } from "idb";
import type { Attempt } from "../types/course";
import { CONTENT_VERSION, isKnownContentVersion } from "../content/version";
import { assertBackupStructure } from "../lib/backup";

export interface ProgressData {
  schemaVersion: 1;
  contentVersion: string;
  read: string[];
  drafts: Record<string, Record<string, string>>;
  attempts: Attempt[];
  activeSeconds: number;
  days: string[];
}
export const emptyProgress = (): ProgressData => ({
  schemaVersion: 1,
  contentVersion: CONTENT_VERSION,
  read: [],
  drafts: {},
  attempts: [],
  activeSeconds: 0,
  days: [],
});
const db = () =>
  openDB("fls-learning", 1, {
    upgrade(database) {
      database.createObjectStore("progress");
    },
  });
let pending: ReturnType<typeof setTimeout> | undefined;
let writeQueue = Promise.resolve();
interface ProgressStore {
  data: ProgressData;
  hydrated: boolean;
  saveState: "saved" | "saving" | "error";
  persistence: "ready" | "protected" | "unavailable";
  storageMessage: string;
  protectedBackup?: unknown;
  hydrate: () => Promise<void>;
  update: (fn: (data: ProgressData) => ProgressData) => void;
  flush: () => Promise<void>;
}
export const useProgress = create<ProgressStore>((set, get) => ({
  data: emptyProgress(),
  hydrated: false,
  saveState: "saved",
  persistence: "ready",
  storageMessage: "",
  async hydrate() {
    try {
      const database = await db();
      const stored: unknown = await database.get("progress", "current");
      if (stored) {
        try {
          const data = parseBackup(stored);
          const upgrading =
            (stored as { contentVersion: string }).contentVersion !==
            CONTENT_VERSION;
          if (upgrading) {
            // 归档与迁移写入使用同一事务；归档失败就不能覆盖原记录。
            const transaction = database.transaction("progress", "readwrite");
            await transaction.store.put(
              stored,
              `archive:${Date.now()}:${crypto.randomUUID()}`,
            );
            await transaction.store.put(data, "current");
            await transaction.done;
          }
          set({
            data,
            persistence: "ready",
            saveState: "saved",
            storageMessage: upgrading
              ? "课程已升级：草稿和历史测评已保留，原记录已归档。旧成绩不用于新版完成状态；旧版未交卷作答仍可导出，请开始新版测评。"
              : "",
            protectedBackup: upgrading ? stored : undefined,
          });
        } catch (error) {
          set({
            hydrated: true,
            persistence: "protected",
            saveState: "error",
            protectedBackup: stored,
            storageMessage: `检测到无法兼容的旧记录，已禁止覆盖原数据库。${error instanceof Error ? error.message : ""} 新练习仅存内存，请在学习记录页分别导出备份。`,
          });
          return;
        }
      }
      set({ hydrated: true, persistence: "ready" });
    } catch {
      set({
        hydrated: true,
        saveState: "error",
        persistence: "unavailable",
        storageMessage:
          "当前浏览器无法使用本地数据库。可以继续练习，但刷新前请导出内存中的学习备份。",
      });
    }
  },
  update(fn) {
    set({
      data: fn(get().data),
      saveState: get().persistence === "ready" ? "saving" : "error",
    });
    clearTimeout(pending);
    pending = setTimeout(() => {
      void get().flush();
    }, 800);
  },
  async flush() {
    clearTimeout(pending);
    if (get().persistence !== "ready") return;
    const snapshot = get().data;
    // 串行写入防止较旧的异步保存覆盖较新的草稿。
    writeQueue = writeQueue
      .catch(() => {})
      .then(async () => {
        if (get().persistence !== "ready") return;
        try {
          await (await db()).put("progress", snapshot, "current");
          if (get().data === snapshot) set({ saveState: "saved" });
        } catch {
          set({
            saveState: "error",
            storageMessage:
              "保存失败，内存中的草稿仍然保留。请导出备份后再刷新。",
          });
        }
      });
    return writeQueue;
  },
}));
export function parseBackup(value: unknown): ProgressData {
  if (!value || typeof value !== "object") throw new Error("备份格式不正确");
  const data = value as Partial<ProgressData>;
  if (data.schemaVersion !== 1 || !isKnownContentVersion(data.contentVersion))
    throw new Error("备份版本与当前课程不匹配，未覆盖现有进度");
  assertBackupStructure(value);
  if (
    data.attempts?.some(
      (attempt) => !isKnownContentVersion(attempt.contentVersion),
    )
  )
    throw new Error("测评记录含无法兼容的课程版本，未覆盖现有进度");
  if (
    !Array.isArray(data.read) ||
    !data.read.every((item) => typeof item === "string") ||
    !data.drafts ||
    typeof data.drafts !== "object" ||
    !Array.isArray(data.attempts) ||
    !Array.isArray(data.days) ||
    !data.days.every((day) => /^\d{4}-\d{2}-\d{2}$/.test(day)) ||
    typeof data.activeSeconds !== "number" ||
    !Number.isFinite(data.activeSeconds) ||
    data.activeSeconds < 0
  )
    throw new Error("备份字段不合法");
  for (const files of Object.values(data.drafts))
    if (
      !files ||
      typeof files !== "object" ||
      Object.values(files).some((code) => typeof code !== "string")
    )
      throw new Error("草稿字段不合法");
  // 已知旧版保留学习草稿与历史成绩，但旧版通过不能授予新版扩充后的模块完成。
  return {
    ...structuredClone(data),
    contentVersion: CONTENT_VERSION,
  } as ProgressData;
}
export function isComplete(
  lessonId: string,
  moduleQuizId: string,
  data: ProgressData,
  passScorePercent = 70,
) {
  return (
    data.read.includes(lessonId) &&
    data.attempts.some(
      (attempt) =>
        attempt.quizId === moduleQuizId &&
        attempt.contentVersion === CONTENT_VERSION &&
        attempt.mode === "full" &&
        attempt.status === "submitted" &&
        attempt.results.length > 0 &&
        attempt.results.reduce((sum, item) => sum + item.earned, 0) /
          attempt.results.reduce((sum, item) => sum + item.possible, 0) >=
          passScorePercent / 100,
    )
  );
}
